import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { Pencil, Megaphone, Users, ShoppingBag, LogOut } from "lucide-react";
import Login from "./Login";
import BlogDashboard from "./BlogDashboard"; // New Blog Dashboard Page
import Blog from "./Blog";
import DeleteBlog from "./DeleteBlog";
import Announcement from "./Announcement";
import Membership from "./Membership"; // Import it
import 'antd/dist/reset.css';

// Inside <Routes>

export default function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user"); // Remove when logging out
    }
  }, [user]);

  return (
    <Router>
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {/* Navbar */}
        <nav className="bg-gray-900 text-white p-4 flex justify-between items-center shadow-md">
          <h1 className="text-2xl font-bold">Pavan's Badminton Academy Admin Dashboard</h1>
          {user ? (
            <button
              onClick={() => setUser(null)}
              className="flex items-center gap-2 bg-red-600 px-4 py-2 rounded hover:bg-red-500 transition"
            >
              <LogOut size={20} /> Logout
            </button>
          ) : (
            <Login onLogin={setUser} />
          )}
        </nav>

        {/* Show Dashboard if logged in, else show login prompt */}
        {user ? (
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/blogs" element={<BlogDashboard />} />
            <Route path="/blogs/create" element={<Blog />} />
            <Route path="/blogs/delete" element={<DeleteBlog/>} />
            <Route path="/announcements" element={<Announcement/>} />
            <Route path="/membership" element={<Membership />} />
          </Routes>
        ) : (
          <div className="flex justify-center items-center h-screen text-lg font-semibold text-gray-700">
            Please Login to Access the Dashboard
          </div>
        )}
      </div>
    </Router>
  );
}

// Dashboard with navigation buttons
function Dashboard() {
  return (
    <div className="flex flex-col items-center justify-center p-10">
      <div className="grid grid-cols-2 gap-8 w-full max-w-3xl">
        <DashboardButton icon={<Pencil size={40} />} text="Blogs" to="/blogs" />
        <DashboardButton icon={<Megaphone size={40} />} text="Announcements" to="/announcements" />
        <DashboardButton icon={<Users size={40} />} text="Membership" to="/membership"/>
        <DashboardButton icon={<ShoppingBag size={40} />} text="Shop" to="/shop" disabled/>
      </div>
    </div>
  );
}

// Button Component for Dashboard Navigation
function DashboardButton({ icon, text, to, disabled = false }) {
  const navigate = useNavigate();

  return (
    <div className="relative group w-full">
      <button
        onClick={() => !disabled && navigate(to)}
        className={`w-full h-40 flex flex-col justify-center items-center border-2 rounded-lg bg-transparent transition-all text-lg font-semibold shadow-md ${
          disabled
            ? "border-gray-300 text-gray-400 cursor-not-allowed opacity-50"
            : "border-gray-400 hover:border-gray-600 text-gray-900 hover:shadow-xl"
        }`}
        disabled={disabled}
      >
        {icon}
        <span className="mt-2">{text}</span>
      </button>

      {/* Tooltip for disabled buttons */}
      {disabled && (
        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
          Coming Soon 🚀
        </div>
      )}
    </div>
  );
}
